import { IconButton } from '@/components/basic/icon-button';
import { UserSettingsDropdown } from '@/components/compound-components/settings/users-tab/user-settings-dropdown';
import { UserSettingsDropdownCheckboxItem } from '@/components/compound-components/settings/users-tab/user-settings-dropdown-checkbox-item';
import { DropdownMenuSeparator } from '@/components/shadcn/dropdown-menu';
import { Input } from '@/components/shadcn/input';
import { Cross1Icon, PersonIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import TrashIcon from '@/assets/icons/trash.svg';
import {
  User,
  UserRoles,
  removeUser,
  removeUserRole,
  useRoleAdder,
  useUserAdder,
} from '@/lib/auth-service';
import { useOrganizationContext } from '@/contexts/organization-context';
import { Spinner } from '@/components/basic/spinner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/shadcn/button';
import { useToast } from '@/components/shadcn/use-toast';

// TODO: This entire thing needs to be refactored
export function UserSettingsRow({
  user,
  updateContactPoints,
  refreshUsers,
  setAddingUser,
}: {
  user: User;
  updateContactPoints: (user: User, isDeleting?: boolean) => Promise<void>;
  refreshUsers: () => void;
  setAddingUser?: (value: boolean) => void;
}) {
  const [rolesValue, setRolesValue] = useState<string>('');
  const [rolesList, setRolesList] = useState<UserRoles[]>(user.roles || []); // ['Org Admin', 'Social Manager', 'Grants Manager'
  const [notificationsValue, setNotificationsValue] = useState<string>('');
  const [notificationsObject, setNotificationsObject] = useState(user.updates);

  const [creatingUser, setCreatingUser] = useState<boolean>(false);
  const [isConfirmDelete, setIsConfirmDelete] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');

  const { organizationId } = useOrganizationContext();
  const { addUserRoles } = useRoleAdder(
    organizationId as string,
    user.id as string
  );
  const { addUser } = useUserAdder(organizationId as string);

  const { toast } = useToast();

  useEffect(() => {
    const notifications = [];
    if (notificationsObject.grants) notifications.push('Grants');
    if (notificationsObject.socialMedia) notifications.push('Social Media');
    setNotificationsValue(notifications.join(', ') || 'Select updates');
  }, [notificationsObject]);

  useEffect(() => {
    setRolesValue(mapRolesToString(rolesList));
  }, [rolesList]);

  const validateEmail = useCallback(
    (email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !email.trim() || !emailRegex.test(email)) {
        toast({
          title: 'Error',
          description: 'Please enter a valid email address',
          variant: 'destructive',
        });
        return false
      }
      return true
    },
    [toast]
  );

  const handleRoleChange = (role: UserRoles) => {
    if (user.id) {
      setRolesList((prev) => {
        const isSelected = prev.includes(role);
        if (isSelected) {
          removeUserRole(
            organizationId as string,
            user.id as string,
            role
          ).finally(() => {
            refreshUsers();
          });
          return prev.filter((r) => r !== role);
        } else {
          addUserRoles({ roles: [role] }).finally(() => {
            refreshUsers();
          });
          return [...prev, role];
        }
      });
    } else {
      // Validate email before creating user
      if (!validateEmail(newEmail)) return;

      // create user
      setCreatingUser(true);
      setRolesList([role]);
      addUser({ email: newEmail, roles: [role] }).finally(() => {
        setAddingUser && setAddingUser(false);
        setCreatingUser(false);
        refreshUsers();
      });
    }
  };

  const handleNotificationsChange = (key: 'grants' | 'socialMedia') => {
    // Validate email before creating user
    if (!user.id && !validateEmail(newEmail)) return;

    setNotificationsObject((prev) => {
      updateContactPoints({
        ...user,
        email: user.email ? user.email : newEmail,
        updates: { ...prev, [key]: !prev[key] },
      });
      setAddingUser && setAddingUser(false);
      return { ...prev, [key]: !prev[key] };
    });
  };

  const deleteUser = async () => {
    // delete user
    await removeUser(organizationId as string, user.id as string);
    // remove contact points
    await updateContactPoints(user, true);
    await refreshUsers();
  };

  const mapRolesToString = (roles: UserRoles[]) => {
    const roleMap: { [key in UserRoles]: string } = {
      [UserRoles.ORGANIZATION_MANAGER]: 'Org Admin',
      [UserRoles.SOCIAL_MEDIA_MANAGER]: 'Social Manager',
      [UserRoles.GRANTS_MANAGER]: 'Grants Manager',
      [UserRoles.ADMIN]: 'admin',
    };
    return roles.map((role) => roleMap[role]).join(', ') || 'Select role';
  };

  return (
    <div className='relative'>
      <div className='grid grid-cols-20 items-center gap-2'>
        <div className='hidden md:block'>
          <PersonIcon
            className={cn(
              'w-full',
              isConfirmDelete ? 'text-destructive' : 'text-maggie'
            )}
          />
        </div>
        {isConfirmDelete ? (
          <div className='col-span-18 flex items-center justify-between rounded-md bg-destructive py-1 pl-3 pr-1 text-destructive-foreground'>
            <p className='truncate'>
              Are you sure you want to delete {user.email}?
            </p>
            <Button
              className='disabled:opacity-1 h-fit bg-destructive-foreground px-2 py-0.5 text-destructive hover:bg-destructive-foreground/90 disabled:bg-destructive-foreground/80'
              disabled={isDeleting}
              onClick={async (e) => {
                e.stopPropagation();
                setIsDeleting(true);
                await deleteUser();
                setIsConfirmDelete(false);
                setIsDeleting(false);
              }}
            >
              {isDeleting ? <Spinner className='h-6 w-6' /> : 'Yes, Delete'}
            </Button>
          </div>
        ) : (
          <>
            <div className='col-span-6'>
              <Input
                type='email'
                placeholder='Add email here...'
                className='disabled:opacity-1 border-gray-settings-button focus-visible:ring-maggie-500 disabled:bg-gray-settings-button/80'
                value={user.email || newEmail}
                disabled={user.email !== ''}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                }}
              />
            </div>
            <div className='col-span-6'>
              <UserSettingsDropdown value={rolesValue}>
                <UserSettingsDropdownCheckboxItem
                  checked={rolesList.includes(UserRoles.ORGANIZATION_MANAGER)}
                  onCheckedChange={() => {
                    handleRoleChange(UserRoles.ORGANIZATION_MANAGER);
                  }}
                >
                  Org Admin
                </UserSettingsDropdownCheckboxItem>
                <DropdownMenuSeparator className='my-0' />
                <UserSettingsDropdownCheckboxItem
                  checked={rolesList.includes(UserRoles.SOCIAL_MEDIA_MANAGER)}
                  onCheckedChange={() => {
                    handleRoleChange(UserRoles.SOCIAL_MEDIA_MANAGER);
                  }}
                >
                  Social Manager
                </UserSettingsDropdownCheckboxItem>
                <DropdownMenuSeparator className='my-0' />
                <UserSettingsDropdownCheckboxItem
                  checked={rolesList.includes(UserRoles.GRANTS_MANAGER)}
                  onCheckedChange={() => {
                    handleRoleChange(UserRoles.GRANTS_MANAGER);
                  }}
                >
                  Grants Manager
                </UserSettingsDropdownCheckboxItem>
              </UserSettingsDropdown>
            </div>
            <div className='col-span-6'>
              <UserSettingsDropdown value={notificationsValue}>
                <UserSettingsDropdownCheckboxItem
                  checked={notificationsObject.grants}
                  onCheckedChange={() => {
                    handleNotificationsChange('grants');
                  }}
                >
                  Grants
                </UserSettingsDropdownCheckboxItem>
                <DropdownMenuSeparator className='my-0' />
                <UserSettingsDropdownCheckboxItem
                  checked={notificationsObject.socialMedia}
                  onCheckedChange={() => {
                    handleNotificationsChange('socialMedia');
                  }}
                >
                  Social Media
                </UserSettingsDropdownCheckboxItem>
              </UserSettingsDropdown>
            </div>
          </>
        )}
        <div className='col-span-2 h-full w-full md:col-span-1'>
          {isConfirmDelete ? (
            <Button
              className='h-full max-h-10 w-full max-w-10 border-none p-0 shadow-none hover:bg-transparent'
              variant='secondary'
              size='icon-lg'
              onClick={(e) => {
                e.stopPropagation();
                setIsConfirmDelete(false);
              }}
            >
              <Cross1Icon className='text-[#707070]' />
            </Button>
          ) : (
            user.email && (
              <IconButton
                className='h-full max-h-10 w-full max-w-10 border-none p-0 shadow-none hover:bg-transparent'
                src={TrashIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsConfirmDelete(true);
                }}
              />
            )
          )}
        </div>
        {creatingUser && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-gray-settings/50'>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
}
